import { Controller } from "stimulus";

// Tracks a real ContentSyncRun. Delayed Job has no browser-facing lifecycle,
// so the controller starts a JSON run then polls its narrow status endpoint.
export default class extends Controller {
  static targets = ["form", "button", "label", "stamp", "dot", "summary", "issues", "issuesSummary", "issuesList"];
  static values = {
    state: String,
    unsyncedChanges: Number,
    confirmMessage: String,
    totalTargets: Number,
    succeededTargets: Number,
    attentionTargets: Number,
    finishedAt: String,
    targetIssues: Array,
  };

  connect() {
    // Keep Rails UJS confirmation as the no-JavaScript fallback, but avoid
    // two prompts when this controller takes over the form submission.
    delete this.formTarget.dataset.confirm;
    this.renderState(this.stateValue || "pending", this.unsyncedChangesValue, this.finishedAtValue, this.initialRunDetails);
  }

  async start(event) {
    event.preventDefault();
    if (this.syncing || this.buttonTarget.classList.contains("is-synced")) return;
    if (this.hasConfirmMessageValue && !window.confirm(this.confirmMessageValue)) return;

    this.syncing = true;
    this.renderState("syncing");

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        body: new FormData(this.formTarget),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const run = await response.json();
      await this.poll(run.status_url);
    } catch (_error) {
      this.syncing = false;
      this.renderState("failed");
    }
  }

  async poll(statusUrl) {
    const response = await fetch(statusUrl, { credentials: "same-origin", headers: { "Accept": "application/json" } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const run = await response.json();

    if (!run.complete) {
      this.pollTimer = setTimeout(() => this.poll(statusUrl).catch(() => this.renderState("failed")), 1500);
      return;
    }

    this.syncing = false;
    if (run.current_matches_run) {
      const state = run.status === "succeeded" ? "synced" : run.status;
      this.renderState(state, run.unsynced_changes, run.finished_at, run);
    } else {
      this.renderState("pending", run.unsynced_changes);
    }
  }

  disconnect() {
    clearTimeout(this.pollTimer);
  }

  renderState(state, changes = this.unsyncedChangesValue, finishedAt = null, run = {}) {
    this.buttonTarget.classList.remove("is-pending", "is-syncing", "is-synced", "is-partial", "is-failed");
    this.buttonTarget.classList.add(`is-${state}`);
    this.buttonTarget.disabled = state === "syncing" || state === "synced";

    if (state === "syncing") {
      this.labelTarget.textContent = "Syncing…";
      this.stampTarget.textContent = "";
      this.dotTarget.textContent = "";
      this.dotTarget.hidden = true;
      this.setSummary("Pushing changes to CAPP…", "");
      this.renderIssues([]);
    } else if (state === "synced") {
      this.labelTarget.textContent = "Synced";
      this.stampTarget.textContent = finishedAt ? `· ${new Date(finishedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "";
      this.dotTarget.hidden = true;
      this.setSummary("in sync with CAPP", "is-synced");
      this.renderIssues([]);
    } else if (state === "partial") {
      const attention = run.attention_targets || run.failed_targets + run.partial_targets || 1;
      this.labelTarget.textContent = "Retry sync";
      this.stampTarget.textContent = "";
      this.dotTarget.hidden = false;
      this.dotTarget.textContent = attention;
      this.setSummary(`${run.succeeded_targets} of ${run.total_targets} CAPP tenants synced · ${attention} ${attention === 1 ? "tenant needs" : "tenants need"} retry`, "is-partial");
      this.renderIssues(run.target_issues || []);
    } else if (state === "failed") {
      this.labelTarget.textContent = "Sync needs attention";
      this.stampTarget.textContent = "";
      this.dotTarget.hidden = !(changes > 0);
      this.dotTarget.textContent = changes || "!";
      this.setSummary("sync needs attention", "is-failed");
      this.renderIssues(run.target_issues || []);
    } else {
      this.labelTarget.textContent = "Sync to CAPP";
      this.stampTarget.textContent = "";
      this.dotTarget.hidden = !(changes > 0);
      this.dotTarget.textContent = changes || "";
      this.setSummary(changes > 0 ? `${changes} unsynced change${changes === 1 ? "" : "s"}` : "ready to sync with CAPP", changes > 0 ? "is-pending" : "");
      this.renderIssues([]);
    }
  }

  setSummary(text, className) {
    const bulletinCount = this.summaryTarget.dataset.bulletinCount;
    const status = document.createElement("span");
    status.className = className;
    status.textContent = text;
    this.summaryTarget.replaceChildren(document.createTextNode(`${bulletinCount} · `), status);
  }

  renderIssues(issues) {
    this.issuesTarget.hidden = issues.length === 0;
    if (issues.length === 0) return;

    this.issuesSummaryTarget.textContent = `${issues.length} CAPP ${issues.length === 1 ? "tenant needs" : "tenants need"} attention`;
    this.issuesListTarget.replaceChildren(...issues.map((issue) => {
      const item = document.createElement("li");
      const customer = document.createElement("a");
      customer.href = issue.customer_path || `/customers/${issue.organization_id}`;
      customer.textContent = issue.organization_name;
      const status = document.createElement("strong");
      status.textContent = issue.status.replace(/_/g, " ");
      item.append(customer, document.createTextNode(" · "), status, document.createTextNode(`: ${issue.reason}`));
      return item;
    }));
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || "";
  }

  get initialRunDetails() {
    return {
      total_targets: this.totalTargetsValue,
      succeeded_targets: this.succeededTargetsValue,
      attention_targets: this.attentionTargetsValue,
      target_issues: this.targetIssuesValue,
    };
  }
}
